import type { MedplumClient } from "@medplum/core";
import type { Basic } from "@medplum/fhirtypes";
import { FhirResource } from "@vibrant/models/fhir/FhirResource";
import type { ValueOf } from "@vibrant/models/fhir/fhir-resource-types";

//==================================================================================================
//                                     VIBRANT PERMISSIONS
//==================================================================================================
export const Permission = {
  Labs: {
    CreateOrder: {
      id: "labs.create-order",
      displayName: "Create Order",
      description: "Ability to create a new order for a lab test",
    },
    SendOrder: "labs.send-order",
  },
  Supplements: {
    View: "supplements.view",
    Manage: "supplements.manage",
  },
} as const;

export const VibrantRoles = {
  Admin: "admin",
  PrescribingProvider: "prescribing-provider",
  NonPrescribingProvider: "non-prescribing-provider",
  Contact: "contact",
} as const;

export type RoleType = ValueOf<typeof VibrantRoles>;

//==================================================================================================
//                                     ROLE DEFINITION MODEL
//==================================================================================================
export class RoleDefinition extends FhirResource<Basic> {
  static readonly ResourceType = "Basic";
  static readonly Code = {
    system: "vibrant.resource-type",
    code: "RoleDefinition",
    display: "Role Definition",
  };

  //------------------------------------------------------------------------------------------------
  //                                     VIRTUAL PROPERTIES
  //------------------------------------------------------------------------------------------------
  /**
   * Returns the role ID
   */
  get roleId(): string {
    const identifier = this.resource.identifier?.find((i) => i.system === "roleId");
    return identifier?.value || "";
  }

  /**
   * Returns the display name of the role
   */
  get name(): string {
    return this.getExtension("name")?.valueString || "unknown";
  }

  /**
   * Returns the description of the role
   */
  get description(): string {
    return this.getExtension("description")?.valueString || "unknown";
  }

  /**
   * Enabled permissions for the role.
   */
  get permissions(): Array<{ id: string; enabled: boolean; displayName: string }> {
    const permissions = this.getExtension("permissions")?.extension || [];

    return permissions.map((permission) => ({
      id: permission.url,
      enabled: permission.extension?.find((e) => e.url === "enabled")?.valueBoolean || false,
      displayName: permission.extension?.find((e) => e.url === "name")?.valueString || "",
    }));
  }

  //------------------------------------------------------------------------------------------------
  //                                      STATIC METHODS
  //------------------------------------------------------------------------------------------------

  /**
   * Creates a new role definition
   */
  static async create(
    medplum: MedplumClient,
    data: {
      name: string;
      description: string;
      roleId: string;
      permissions: Array<{ id: string; name: string; description: string }>;
    },
  ): Promise<RoleDefinition> {
    const permissionsExtension = data.permissions.map((p) => ({
      url: p.id,
      extension: [
        {
          url: "name",
          valueString: p.name,
        },
        {
          url: "enabled",
          valueBoolean: true,
        },
        {
          url: "description",
          valueString: p.description,
        },
      ],
    }));

    const resource = await RoleDefinition.createResource(medplum, {
      code: { coding: [RoleDefinition.Code] },
      identifier: [
        {
          system: "roleId",
          value: data.roleId,
        },
      ],
      extension: [
        {
          url: "name",
          valueString: data.name,
        },
        {
          url: "description",
          valueString: data.description,
        },
        {
          url: "permissions",
          extension: permissionsExtension,
        },
      ],
    });

    return new RoleDefinition(resource);
  }

  /**
   * Searches for a role definition by ID
   *
   * @param medplum - The Medplum client
   * @param roleId - The ID of the role definition
   * @returns A RoleDefinition object or undefined if not found
   */
  static async getByRoleId(
    medplum: MedplumClient,
    roleId: string,
  ): Promise<RoleDefinition | undefined> {
    const resource = await RoleDefinition.searchOneResource(medplum, {
      code: RoleDefinition.Code.code,
      identifier: `roleId|${roleId}`,
    });
    return resource ? new RoleDefinition(resource) : undefined;
  }

  /**
   * Searches for all role definitions.
   *
   * @param medplum - The Medplum client
   * @returns An array of RoleDefinition objects
   */
  static async search(medplum: MedplumClient): Promise<RoleDefinition[]> {
    const resources = await RoleDefinition.searchResources(medplum, {
      code: RoleDefinition.Code.code,
    });
    return resources.map((resource) => new RoleDefinition(resource));
  }

  //------------------------------------------------------------------------------------------------
  //                                     INSTANCE METHODS
  //------------------------------------------------------------------------------------------------

  /**
   * Checks if the role has a specific permission
   */
  hasPermission(permissionId: string): boolean {
    const permission = this.permissions.find((p) => p.id === permissionId);
    return permission?.enabled || false;
  }

  /**
   * Creates a new RoleDefinition instance with an updated roleId.
   * Used for optimistic UI updates.
   *
   * @param roleId - The new role ID.
   * @returns A new RoleDefinition instance.
   */
  withRoleId(roleId: string): RoleDefinition {
    // Map roleId to display name for optimistic updates
    const roleNames: Record<string, string> = {
      [VibrantRoles.Admin]: "Admin",
      [VibrantRoles.PrescribingProvider]: "Prescriber",
      [VibrantRoles.NonPrescribingProvider]: "Non-Prescriber",
      [VibrantRoles.Contact]: "Contact",
    };

    const updatedResource: Basic = {
      ...this.resource,
      identifier: [{ system: "roleId", value: roleId }],
      extension: [
        { url: "name", valueString: roleNames[roleId] ?? roleId },
        ...(this.resource.extension?.filter((ext) => ext.url !== "name") ?? []),
      ],
    };

    return new RoleDefinition(updatedResource);
  }
}
